import { useEffect, useMemo } from 'react';
import {
  MapEntityType,
  Operation,
  selectMapViewport,
  selectSelectedStopAreaId,
  selectSelectedTerminalId,
  selectShowMapEntityTypes,
  useAppSelector,
  useLoader,
} from '../../../redux';
import { LoadingState } from '../../../types';
import { useFilterStopAreas } from '../StopAreas';
import { useGetRoutesDisplayedInMap } from '../Stops/utils';
import { isViewportLoaded } from '../Utils/isViewportLoaded';
import { useFilterStops } from '../Utils/useFilterStops';
import { useGetMapDepotStops } from './useGetMapDepotStops';
import { useGetMapStopAreas } from './useGetMapStopAreas';
import { useGetMapStops } from './useGetMapStops';
import { useGetMapTerminals } from './useGetMapTerminals';

export function useGetMapData() {
  const viewport = useAppSelector(selectMapViewport);
  const viewportIsLoaded = isViewportLoaded(viewport);
  const filterByUiFiltersAndRoute = useFilterStops();

  const {
    [MapEntityType.Stop]: showStops,
    [MapEntityType.StopArea]: showStopAreas,
    [MapEntityType.Terminal]: showTerminals,
  } = useAppSelector(selectShowMapEntityTypes);

  const selectedStopAreaId = useAppSelector(selectSelectedStopAreaId);
  const selectedTerminalId = useAppSelector(selectSelectedTerminalId);

  const skipFetchingStops = !showStops || !viewportIsLoaded;
  const { stops, loading: stopsAreLoading } = useGetMapStops({
    viewport,
    selectedStopAreaId,
    selectedTerminalId,
    skipFetching: skipFetchingStops,
  });

  const skipFetchingAreas = !showStopAreas || !viewportIsLoaded;
  const { areas, loading: areasAreLoading } = useGetMapStopAreas({
    skipFetching: skipFetchingAreas,
    viewport,
  });

  const skipFetchingTerminals = !showTerminals || !viewportIsLoaded;
  const { terminals, loading: terminalsAreLoading } = useGetMapTerminals({
    skipFetching: skipFetchingTerminals,
    viewport,
  });

  const skipFetchingDepotStops = !showStops || !viewportIsLoaded;
  const { depotStops, loading: depotStopsAreLoading } = useGetMapDepotStops({
    skipFetching: skipFetchingDepotStops,
    viewport,
  });

  const { displayedRouteIds, loading: displayedRoutesAreLoading } =
    useGetRoutesDisplayedInMap();

  const { setLoadingState } = useLoader(Operation.LoadMapData, {
    initialState: LoadingState.HighPriority,
  });

  const loading =
    stopsAreLoading ||
    areasAreLoading ||
    terminalsAreLoading ||
    depotStopsAreLoading ||
    displayedRoutesAreLoading;
  useEffect(() => {
    setLoadingState(
      loading ? LoadingState.MediumPriority : LoadingState.NotLoading,
    );
  }, [loading, setLoadingState]);

  const filteredStops = useMemo(
    () => filterByUiFiltersAndRoute(stops),
    [filterByUiFiltersAndRoute, stops],
  );

  const filteredAreas = useFilterStopAreas(areas);

  return {
    stops: filteredStops,
    areas: filteredAreas,
    displayedRouteIds,
    terminals,
    depotStops,
  };
}
